// Pure helpers for the workout planner. No React, no Redux, no side effects.
// Dates are handled strictly as local calendar dates (YYYY-MM-DD); JS Date is
// only used to step/format, avoiding UTC shifts and month-overflow surprises.

export const ZONES = [
    ['recovery', 'Восстановление'],
    ['endurance', 'Аэробная'],
    ['tempo', 'Темп'],
    ['sweet_spot', 'Sweet Spot'],
    ['threshold', 'Порог'],
    ['vo2max', 'VO2max'],
    ['anaerobic', 'Анаэробная'],
]

export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
export const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
export const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

export function parseLocal(s) {
    const p = s.split('-')
    return new Date(+p[0], +p[1] - 1, +p[2])
}

export function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function addDays(s, n) {
    const d = parseLocal(s)
    d.setDate(d.getDate() + n)
    return fmtDate(d)
}

export function todayLocal() {
    return fmtDate(new Date())
}

export function dowMon(s) {
    return (parseLocal(s).getDay() + 6) % 7
}

export function mondayOf(s) {
    return addDays(s, -dowMon(s))
}

export function addMonths(s, n) {
    const d = parseLocal(s)
    const day = d.getDate()
    d.setDate(1)
    d.setMonth(d.getMonth() + n)
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
    d.setDate(Math.min(day, last))
    return fmtDate(d)
}

export function firstOfMonth(s) {
    const d = parseLocal(s)
    return fmtDate(new Date(d.getFullYear(), d.getMonth(), 1))
}

export function weekdayShort(s) {
    return WEEKDAYS_SHORT[dowMon(s)]
}

export function fmtLong(s) {
    const d = parseLocal(s)
    return `${WEEKDAYS_SHORT[dowMon(s)]}, ${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

export function fmtShort(s) {
    const d = parseLocal(s)
    return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

export function fmtMonthTitle(viewMonth) {
    const d = parseLocal(viewMonth)
    return `${MONTHS_NOM[d.getMonth()]} ${d.getFullYear()}`
}

export function isValidDate(s) {
    if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
    return fmtDate(parseLocal(s)) === s
}

export function monthWeeks(viewMonth) {
    const d0 = parseLocal(firstOfMonth(viewMonth))
    const last = fmtDate(new Date(d0.getFullYear(), d0.getMonth() + 1, 0))
    const weeks = []
    let cur = mondayOf(viewMonth)
    while (cur <= last) {
        const week = []
        for (let i = 0; i < 7; i += 1) week.push(addDays(cur, i))
        weeks.push(week)
        cur = addDays(cur, 7)
    }
    return weeks
}

export function monthGridDates(viewMonth) {
    return monthWeeks(viewMonth).flat()
}

export function monthRange(viewMonth) {
    const grid = monthGridDates(viewMonth)
    if (!grid.length) return null
    return { dateFrom: grid[0], dateTo: grid[grid.length - 1] }
}

export function computeRepeat(startDate, weekdays, weeks) {
    const errors = []
    if (!isValidDate(startDate)) {
        errors.push('указана некорректная дата начала')
    }
    if (!Array.isArray(weekdays) || !weekdays.length) {
        errors.push('выберите хотя бы один день недели')
    } else if (!weekdays.every(w => Number.isInteger(w) && w >= 0 && w <= 6)) {
        errors.push('указан недопустимый день недели')
    }
    let weeksCount = null
    if (weeks === '' || weeks === null || weeks === undefined) {
        errors.push('укажите количество недель')
    } else {
        weeksCount = Number(weeks)
        if (
            !Number.isFinite(weeksCount)
            || !Number.isInteger(weeksCount)
            || weeksCount < 1
            || weeksCount > 52
        ) {
            errors.push('количество недель — целое число от 1 до 52')
            weeksCount = null
        }
    }

    if (errors.length) {
        return { ok: false, dates: [], end: '', errors }
    }

    const end = addDays(startDate, 7 * weeksCount)
    const dates = []
    let cur = addDays(startDate, 1)
    while (cur <= end) {
        if (weekdays.includes(dowMon(cur))) dates.push(cur)
        cur = addDays(cur, 1)
    }
    if (!dates.length) {
        errors.push('в выбранном диапазоне нет таких дней недели')
        return { ok: false, dates: [], end, errors }
    }
    return { ok: true, dates, end, errors: [] }
}

export function normalizeActivity(activity, workoutsById) {
    if (!activity || !activity.start_time || !Number.isFinite(activity.start_time)) {
        return null
    }
    const local = new Date(activity.start_time * 1000)
    const workout = workoutsById ? workoutsById[activity.workout_id] : undefined
    const load = typeof activity.tss === 'number' ? activity.tss : null
    return {
        date: fmtDate(local),
        title: activity.name || 'Активность без названия',
        duration: activity.timer_duration != null ? activity.timer_duration : activity.elapsed_duration,
        if_: typeof activity.if_ === 'number' ? activity.if_ : null,
        load,
        unknown: load === null,
        typeKey: workout ? workoutTypeKey(workout) : null,
        workout_id: activity.workout_id,
    }
}

export function dayLoads(plans, normalizedActivities) {
    let plan = 0
    let planN = 0
    let planUnknown = 0
    let fact = 0
    let unknown = 0
    let actN = 0

    for (const p of plans || []) {
        planN += 1
        if (p.workout && typeof p.workout.tss === 'number') plan += p.workout.tss
        else planUnknown += 1
    }
    for (const a of normalizedActivities || []) {
        actN += 1
        if (a.load === null) unknown += 1
        else fact += a.load
    }
    return { plan, planN, planUnknown, fact, unknown, actN }
}

export function aggregateLoads(loadsList) {
    const total = { plan: 0, planN: 0, planUnknown: 0, fact: 0, unknown: 0, actN: 0 }
    for (const ld of loadsList || []) {
        total.plan += ld.plan
        total.planN += ld.planN
        total.planUnknown += ld.planUnknown
        total.fact += ld.fact
        total.unknown += ld.unknown
        total.actN += ld.actN
    }
    return total
}

export function fmtDur(seconds) {
    if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return '—'
    const totalMin = Math.round(Number(seconds) / 60)
    if (totalMin < 60) return `${totalMin} мин`
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    if (m === 0) return `${h} ч`
    return `${h} ч ${m} мин`
}

export function fmtLoad(n) {
    if (n === null || n === undefined) return '—'
    const r = Math.round(Number(n) * 10) / 10
    return String(r).replace('.', ',')
}

export function fmtInt(i) {
    if (i === null || i === undefined) return '—'
    return Number(i).toFixed(2).replace('.', ',')
}

export function fmtDelta(n) {
    return `${n >= 0 ? '+' : '−'}${fmtLoad(Math.abs(n))}`
}

export const DURATION_PRESETS = [
    { id: 'any', label: 'Любая длительность', test: null },
    { id: 'le30', label: 'До 30 мин включительно', test: m => m <= 1800 },
    { id: 'to60', label: 'Более 30 до 60 мин', test: m => m > 1800 && m <= 3600 },
    { id: 'to90', label: 'Более 60 до 90 мин', test: m => m > 3600 && m <= 5400 },
    { id: 'gt90', label: 'Больше 90 мин', test: m => m > 5400 },
]

export const INTENSITY_PRESETS = [
    { id: 'any', label: 'Любая интенсивность', test: null, hint: null },
    { id: 'rec', label: 'Восстановление', test: v => v <= 0.55, hint: 'Коэффициент до 0,55 включительно' },
    { id: 'aero', label: 'Аэробная', test: v => v > 0.55 && v <= 0.75, hint: 'Коэффициент выше 0,55 и до 0,75 включительно' },
    { id: 'tempo', label: 'Темп', test: v => v > 0.75 && v <= 0.87, hint: 'Коэффициент выше 0,75 и до 0,87 включительно' },
    { id: 'ss', label: 'Sweet Spot', test: v => v > 0.87 && v <= 0.94, hint: 'Коэффициент выше 0,87 и до 0,94 включительно' },
    { id: 'thr', label: 'Порог', test: v => v > 0.94 && v <= 1.05, hint: 'Коэффициент выше 0,94 и до 1,05 включительно' },
    { id: 'vo2', label: 'VO₂max', test: v => v > 1.05 && v <= 1.20, hint: 'Коэффициент выше 1,05 и до 1,20 включительно' },
    { id: 'ana', label: 'Анаэробная', test: v => v > 1.20, hint: 'Коэффициент выше 1,20' },
]

export const LOAD_PRESETS = [
    { id: 'any', label: 'Любая нагрузка', test: null },
    { id: 'lt30', label: 'Меньше 30', test: v => v < 30 },
    { id: '30to60', label: 'От 30 до 60, не включая 60', test: v => v >= 30 && v < 60 },
    { id: '60to90', label: 'От 60 до 90, не включая 90', test: v => v >= 60 && v < 90 },
    { id: '90to120', label: 'От 90 до 120, не включая 120', test: v => v >= 90 && v < 120 },
    { id: '120to150', label: 'От 120 до 150 включительно', test: v => v >= 120 && v <= 150 },
    { id: 'gt150', label: 'Больше 150', test: v => v > 150 },
]

export const SORT_OPTIONS = [
    { value: 'title', label: 'Название' },
    { value: 'durationAsc', label: 'Длительность ↑' },
    { value: 'durationDesc', label: 'Длительность ↓' },
    { value: 'intensityAsc', label: 'Интенсивность ↑' },
    { value: 'intensityDesc', label: 'Интенсивность ↓' },
    { value: 'loadAsc', label: 'Нагрузка ↑' },
    { value: 'loadDesc', label: 'Нагрузка ↓' },
    { value: 'type', label: 'Тип' },
]

export const TYPE_OPTIONS = [
    { value: 'all', label: 'Все типы' },
    ...ZONES.map(([value, label]) => ({ value, label })),
    { value: 'none', label: 'Нет данных' },
]

function zoneDominantValue(property) {
    if (!property) return 0
    if (typeof property.val_flt === 'number' && Number.isFinite(property.val_flt) && property.val_flt > 0) {
        return property.val_flt
    }
    if (typeof property.val_int === 'number' && property.val_int > 0) return property.val_int
    return 0
}

export function workoutTypeKey(workout) {
    let best = null
    let bestValue = 0
    for (const [key] of ZONES) {
        const property = (workout.properties || []).find(x => x.name === `time_in_zone: ${key}`)
        const value = zoneDominantValue(property)
        if (value > bestValue) {
            bestValue = value
            best = key
        }
    }
    return best
}

export function workoutTypeLabel(workout) {
    const key = workoutTypeKey(workout)
    if (!key) return null
    const zone = ZONES.find(([value]) => value === key)
    return zone ? zone[1] : key
}

export function matchPlannerQuery(workout, query) {
    const q = (query || '').trim().toLowerCase()
    if (!q) return true
    return (workout.name || '').toLowerCase().includes(q)
        || (workout.author || '').toLowerCase().includes(q)
}

export function matchPresets(workout, filters = {}) {
    const durationPreset = DURATION_PRESETS.find(p => p.id === filters.durationPreset)
    if (durationPreset && durationPreset.test) {
        if (workout.duration_s === null || workout.duration_s === undefined) return false
        if (!durationPreset.test(workout.duration_s)) return false
    }
    const intensityPreset = INTENSITY_PRESETS.find(p => p.id === filters.intensityPreset)
    if (intensityPreset && intensityPreset.test) {
        if (workout.if_ === null || workout.if_ === undefined) return false
        if (!intensityPreset.test(workout.if_)) return false
    }
    const loadPreset = LOAD_PRESETS.find(p => p.id === filters.loadPreset)
    if (loadPreset && loadPreset.test) {
        if (workout.tss === null || workout.tss === undefined) return false
        if (!loadPreset.test(workout.tss)) return false
    }
    return true
}

export function filterPlannerWorkouts(list, filters = {}) {
    const favoriteIds = filters.favoriteIds || []
    return (list || []).filter(workout => {
        if (filters.type && filters.type !== 'all') {
            const typeKey = workoutTypeKey(workout)
            if (filters.type === 'none') {
                if (typeKey !== null) return false
            } else if (typeKey !== filters.type) {
                return false
            }
        }
        if (filters.favOnly && !favoriteIds.includes(workout.id)) return false
        if (!matchPlannerQuery(workout, filters.query)) return false
        if (!matchPresets(workout, filters)) return false
        return true
    })
}

function nullLastCompare(getValue, direction) {
    return (a, b) => {
        const av = getValue(a)
        const bv = getValue(b)
        const aNull = av === null || av === undefined
        const bNull = bv === null || bv === undefined
        if (aNull && bNull) return 0
        if (aNull) return 1
        if (bNull) return -1
        const cmp = av < bv ? -1 : av > bv ? 1 : 0
        return direction === 'desc' ? -cmp : cmp
    }
}

export function sortPlannerWorkouts(list, sortKey) {
    const indexed = (list || []).map((workout, index) => ({ workout, index }))
    const byIndex = (a, b) => a.index - b.index

    switch (sortKey) {
        case 'title':
            indexed.sort((a, b) => (a.workout.name || '').localeCompare(b.workout.name || '', 'ru') || byIndex(a, b))
            break
        case 'durationAsc':
            indexed.sort((a, b) => nullLastCompare(w => w.duration_s, 'asc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'durationDesc':
            indexed.sort((a, b) => nullLastCompare(w => w.duration_s, 'desc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'intensityAsc':
            indexed.sort((a, b) => nullLastCompare(w => w.if_, 'asc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'intensityDesc':
            indexed.sort((a, b) => nullLastCompare(w => w.if_, 'desc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'loadAsc':
            indexed.sort((a, b) => nullLastCompare(w => w.tss, 'asc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'loadDesc':
            indexed.sort((a, b) => nullLastCompare(w => w.tss, 'desc')(a.workout, b.workout) || byIndex(a, b))
            break
        case 'type': {
            indexed.sort((a, b) => {
                const la = workoutTypeLabel(a.workout)
                const lb = workoutTypeLabel(b.workout)
                const aNull = !la
                const bNull = !lb
                if (aNull && bNull) return byIndex(a, b)
                if (aNull) return 1
                if (bNull) return -1
                return la.localeCompare(lb, 'ru') || byIndex(a, b)
            })
            break
        }
        default:
            break
    }
    return indexed.map(x => x.workout)
}